// src/components/tickets/detail/DraftPanel.tsx
//
// The draft panel (Story 4.2 AC 4). A canvas-inset panel that renders a ticket's
// non-null `draft` as QUOTABLE CONTEXT, never editable, labelled BY ROUTE (3.6
// Decision 6): REVIEW → "Yak's draft"; AUTO → "Yak's answer — as spoken on the
// call" (an AUTO draft is the delivered answer, never "pending review" material).
// The panel repeats the ticket's version-pinned KB source links inside it.
//
// "Use draft" (Story 4.3) is the explicit re-copy affordance: it copies the
// draft into the composer's Response buffer (confirming before overwriting
// non-empty text — handled by the composer). It renders only when an onUseDraft
// handler is provided (an OPEN/ANSWERED ticket with a composer). The draft stays
// quotable context here — never an editable field.

import { draftPanelLabel } from '../../../utils/ticketDisplay'
import { KbSourceLinks } from './KbSourcesCard'
import type { YakTicketRoute, YakKbPinWithDocument } from '../../../types/yak'

interface DraftPanelProps {
  draft: string
  route: YakTicketRoute
  pins: YakKbPinWithDocument[]
  // Copies the draft into the composer's Response buffer. Absent (closed ticket)
  // ⇒ no button renders.
  onUseDraft?: () => void
}

export function DraftPanel({
  draft,
  route,
  pins,
  onUseDraft,
}: DraftPanelProps) {
  return (
    <section className="card p-6" aria-labelledby="draft-heading">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2
          id="draft-heading"
          className="text-sm font-semibold uppercase tracking-wide text-gray-500"
        >
          {draftPanelLabel(route)}
        </h2>
        {onUseDraft ? (
          <button
            type="button"
            onClick={onUseDraft}
            className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
          >
            Use draft
          </button>
        ) : null}
      </div>

      {/* Canvas inset — quotable context, never an editable field. */}
      <div className="rounded-md bg-gray-50 p-4 text-sm leading-relaxed text-gray-900 whitespace-pre-wrap">
        {draft}
      </div>

      {pins.length > 0 ? (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Sources
          </p>
          <KbSourceLinks pins={pins} />
        </div>
      ) : null}
    </section>
  )
}
